import * as api from "../api/client";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { Model } from "../types/api";

type ModelSortKey =
  | "id"
  | "displayName"
  | "provider"
  | "status"
  | "usageCount"
  | "totalEstimatedCostUsd"
  | "alpha";
const ACCESSORS: Record<ModelSortKey, (m: Model) => string | number> = {
  id: (m) => m.id,
  displayName: (m) => m.displayName,
  provider: (m) => m.provider,
  status: (m) => m.status,
  usageCount: (m) => m.usageCount,
  totalEstimatedCostUsd: (m) => m.totalEstimatedCostUsd,
  alpha: (m) => m.alpha,
};

export function ModelsPage() {
  const { data: models, loading, error } = useApi(() => api.listModels(), []);
  // Default: most-used model first — useful without clicking (PK 2026-07-25 directive).
  const { sorted, sort, toggleSort } = useSortableRows(models, ACCESSORS, {
    key: "usageCount",
    direction: "desc",
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-slate-500">Loading models...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4" role="alert">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Models</h2>
        <p className="text-sm text-slate-500 mt-1">
          AI model configurations and pricing (read-only)
        </p>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <SortableTh label="ID" sortKey="id" sort={sort} onSort={toggleSort} sticky />
                <SortableTh
                  label="Display Name"
                  sortKey="displayName"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh label="Provider" sortKey="provider" sort={sort} onSort={toggleSort} />
                <SortableTh label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                <SortableTh label="Usage" sortKey="usageCount" sort={sort} onSort={toggleSort} />
                <SortableTh
                  label="Cost (USD)"
                  sortKey="totalEstimatedCostUsd"
                  sort={sort}
                  onSort={toggleSort}
                />
                <SortableTh label="Alpha/Beta" sortKey="alpha" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sorted.map((m, i) => (
                // Composite key, not bare `m.id`: two real dev-DB model docs are known
                // to share the same `id` FIELD value (a stale renamed-model artifact —
                // see the regression test in ModelsPage.test.tsx) while being genuinely
                // distinct Firestore documents with different usage/cost counters. The
                // backend's GET /v1/admin/models has no response_model / doc.id passthrough
                // to give the frontend a truly unique identifier (v2m-backend follow-up:
                // either dedupe the stale doc or expose the real Firestore doc.id) — until
                // then, the sort-stable index disambiguates the React key without hiding
                // the underlying data-quality bug (both rows still render, both are real).
                <tr key={`${m.id}-${i}`} className="group hover:bg-slate-50 transition-colors">
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 text-sm text-slate-900 font-mono">
                    {m.id}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.displayName}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 capitalize">{m.provider}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        m.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.usageCount}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    ${m.totalEstimatedCostUsd.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {m.alpha}/{m.beta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
